import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
interface FlowOverlayProps {
  open: boolean;
  onClose: () => void;
  closeLabel?: string;
  children: React.ReactNode;
}

/**
 * Full-immersion layer over the signup page. It is deliberately temporary:
 * escapable at any point, with a visible exit that promises the answers are
 * kept, so nobody feels trapped inside a flow about sensitive history.
 */
export function FlowOverlay({
  open,
  onClose,
  closeLabel = 'Close. Your answers are saved',
  children
}: FlowOverlayProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    function handleKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKey);
    };
  }, [open, onClose]);
  return <AnimatePresence data-id="element-388" data-mp="2hoz8m5b4o0qx">
      {open ? <motion.div role="dialog" aria-modal="true" aria-label="Join the portal" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.22,
      ease: 'easeOut'
    }} className="fixed inset-0 z-50 overflow-y-auto bg-canvas" data-id="element-389" data-mp="2mhac1i52u4o8">
          <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 8
      }} transition={{
        duration: 0.26,
        ease: 'easeOut'
      }} className="min-h-screen w-full" data-id="element-390" data-mp="1yshittfuaaxb">
            {children}
          </motion.div>

          <button ref={closeRef} type="button" onClick={onClose} className="fixed right-4 top-4 z-10 inline-flex min-h-tap items-center gap-2 rounded-pill border border-rule bg-card px-4 text-small text-ink shadow-card hover:bg-canvas focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 sm:right-6 sm:top-6" data-id="element-391" data-mp="3f8twt6hbnqsc">
            <XIcon aria-hidden="true" className="h-4 w-4" data-id="element-392" data-mp="2zgrkli5ruf33" />
            <span className="hidden sm:inline" data-id="element-393" data-mp="2zh9maub7dlie">{closeLabel}</span>
            <span className="sr-only sm:hidden" data-id="element-394" data-mp="2brywg7ve5bj1">{closeLabel}</span>
          </button>
        </motion.div> : null}
    </AnimatePresence>;
}